/**
 * Routine Threads — local styled-components (Routine Threads, 2026-07-31).
 *
 * Kept in a dedicated module (NOT the 115KB shared `styles.ts`), following the
 * ThreadChoicePanel.styles.ts per-feature convention, so the routine UI is a
 * contained, additive footprint. Two visual contexts share this file:
 *   • the DARK sidebar rail (section headers, paused chip, next-run meta) —
 *     gold-on-dark like the existing ThreadSidebar* components;
 *   • LIGHT panel/banner surfaces (run banner chip, manager panel) — the
 *     ThreadChoicePanel light palette (#fbfaf6 base, gold borders).
 * Transient `$` props throughout so nothing leaks to the DOM.
 */
import styled from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './styles';

// ── sidebar sections (dark rail) ────────────────────────────────────────────

/**
 * Section header row inside the sidebar list. Born as the routines-only
 * header (2026-07-31); since round 3 (2026-08-01) it is the ONE header all
 * three sections wear — "routines" / "group threads" / "threads" — so their
 * typography/spacing/affordance row (icon + lowercase label + count +
 * optional action) can never drift apart. Sits between rows, so it is
 * smaller and quieter than the rail's top header, in the same gold family.
 * Lowercase labels per the rail's platform style — never re-capitalized here.
 */
export const SidebarSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  /* Density via the rail's own variables (2026-09-20). The fallback is what
     the mobile sheet — which renders these same headers — keeps. */
  padding: var(--rail-section-pt, 10px) 8px var(--rail-section-pb, 6px) 8px;
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: var(--rail-section-size, 0.7rem);
  font-weight: 600;
  letter-spacing: 0.02em;
`;

/** Muted count bubble beside the section label ("routines · 3"). */
export const SidebarSectionCount = styled.span`
  color: rgba(182, 146, 76, 0.6);
  font-weight: 500;
`;

/**
 * "manage" affordance in the Routines section header — opens the routine
 * manager panel. Restyled (production feedback 2026-08-01: the bordered pill
 * read as a foreign bright button in the rail): now a quiet ghost text button
 * in the SAME language as SidebarExitFullScreenButton — transparent at rest,
 * gold text one step softer than the section label, hover tint + brighter
 * gold, rounded-rect (no pill). Lowercase label per the rail's platform style
 * ("threads", "exit full screen").
 */
export const RoutineManageButton = styled.button`
  margin-left: auto;
  padding: 2px 7px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: rgba(182, 146, 76, 0.8);
  font-family: inherit;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.08);
  }
  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
`;

/**
 * Small "Paused" pill on a paused routine's sidebar row (and manager rows).
 * Muted gray — a paused routine is dormant, never "hot".
 */
export const RoutinePausedChip = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #9a9a9a;
  background: rgba(148, 148, 148, 0.14);
  border: 1px solid rgba(148, 148, 148, 0.35);
  border-radius: 999px;
`;

/**
 * Title line on a routine's sidebar row: glyph + title + optional Paused
 * chip, one flex row inside the row's main column.
 */
export const RoutineRowTitleLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/**
 * Next-run meta line on a routine's sidebar row ("Next in 2h" / "Paused").
 * Replaces the generic activity meta — same size/family so rows stay aligned.
 */
export const RoutineNextRunMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  color: rgba(182, 146, 76, 0.55);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ── thread header meta (routine thread open) ────────────────────────────────

/**
 * The subtle one-line routine summary under the thread title in the chat
 * header: glyph + "Routine · Every day at 8:00 AM · Next in 2h". Mirrors
 * HeaderSubtitle's quiet weight so it reads as meta, not a second title.
 *
 * Chip-never-truncates contract (docked density round, 2026-08-01): the
 * owner's screenshot showed the Pause chip clipped to "Pau" in the ~340px
 * docked pane — this container used to carry overflow/ellipsis/nowrap
 * itself, so under width pressure it clipped its LAST flex item (the action
 * chip). The ellipsis now lives on the inner TEXT span
 * (RoutineHeaderMetaText, flex: 0 1 auto + min-width: 0) while the glyph
 * and the chip stay `flex: none` — when the line can't fit both, the CHIP
 * wins and the schedule text ellipsizes. Applies in every mode (the bug is
 * width-driven, not docked-specific).
 */
export const RoutineHeaderMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(182, 146, 76, 0.85);
`;

/**
 * The ellipsizing schedule/next-run TEXT inside the routine header meta line
 * — the ONLY part of the line allowed to shrink (see RoutineHeaderMeta's
 * chip-never-truncates contract).
 */
export const RoutineHeaderMetaText = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Inline pause/resume action chip at the END of the routine header meta line
 * (production feedback 2026-08-01: a paused routine's header said "Paused"
 * with NO way to resume — users typed "resume" into the chat instead). ONE
 * small chip, not a toolbar: `$primary` (Resume on a paused routine — the
 * stranded state this exists for) gets a filled gold treatment; the subtle
 * variant (Pause on an active routine) stays a quiet outline. Same gold
 * family as RoutineRunBannerChip so it reads as part of the meta line.
 */
export const RoutineHeaderActionChip = styled.button<{ $primary?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
  padding: 1px 9px;
  font-family: inherit;
  font-size: 10.5px;
  font-weight: ${(p) => (p.$primary ? 700 : 600)};
  line-height: 1.5;
  color: ${(p) => (p.$primary ? '#6b551f' : 'rgba(107, 85, 31, 0.75)')};
  background: ${(p) => (p.$primary ? 'rgba(198, 167, 94, 0.18)' : 'transparent')};
  border: 1px solid ${(p) => (p.$primary ? 'rgba(198, 167, 94, 0.55)' : 'rgba(198, 167, 94, 0.35)')};
  border-radius: 999px;
  cursor: pointer;
  transition: filter 0.12s ease, opacity 0.12s ease;

  &:hover {
    filter: brightness(0.97);
    background: ${(p) => (p.$primary ? 'rgba(198, 167, 94, 0.26)' : 'rgba(198, 167, 94, 0.1)')};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid rgba(182, 146, 76, 0.6);
    outline-offset: 1px;
  }
`;

/**
 * Inline error line under the routine header meta when a header-chip
 * pause/resume fails (the optimistic flip is reverted alongside it).
 */
export const RoutineHeaderActionError = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  color: #a13a3a;
`;

// ── per-run banner chip (light message surface) ─────────────────────────────

/**
 * Banner chip rendered ABOVE an assistant bubble that carries `routineRun`:
 * glyph + "Routine · {name} · {relative time}". Light-surface gold pill, in
 * the ThreadChoicePanel palette so it sits calmly against the message list.
 */
export const RoutineRunBannerChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 80%;
  padding: 3px 10px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #6b551f;
  background: rgba(198, 167, 94, 0.12);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 999px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * The banner's text content (docked bottom-region round 2, 2026-08-02):
 * text-overflow can't ellipsize the anonymous flex item of an inline-flex
 * chip, so at docked widths a long routine name HARD-CLIPPED mid-glyph at
 * the chip's rounded edge. The text gets its own inline box that owns the
 * ellipsis; the glyph beside it stays flex: none (RoutineGlyph's inline
 * style), so the marker survives at every width and only the words shorten.
 */
export const RoutineRunBannerText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ── routine manager panel (body-portal overlay) ─────────────────────────────

/**
 * Full-viewport dim behind the manager panel. Body-portaled at
 * BODY_PORTAL_Z_INDEX (fullscreen burial lesson — see the constant's doc in
 * styles.ts); clicking it closes the panel.
 */
export const RoutineManagerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 12, 6, 0.55);
`;

/** The panel itself — ThreadChoicePanel light palette, wider for rows. */
export const RoutineManagerPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(560px, 94vw);
  max-height: min(72vh, 640px);
  padding: 14px 16px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #3a3327;
  background: #fbfaf6;
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(60, 48, 20, 0.08);
`;

export const RoutineManagerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RoutineManagerTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #6b551f;
`;

export const RoutineManagerCloseButton = styled.button`
  margin-left: auto;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-family: inherit;
  font-size: 14px;
  line-height: 1;
  color: #6b551f;
  background: transparent;
  border: 1px solid rgba(182, 146, 76, 0.4);
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    filter: brightness(0.97);
    background: rgba(198, 167, 94, 0.1);
  }
  &:focus-visible {
    outline: 2px solid rgba(182, 146, 76, 0.6);
    outline-offset: 1px;
  }
`;

export const RoutineManagerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
`;

export const RoutineManagerRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 9px 11px;
  background: #ffffff;
  border: 1px solid rgba(198, 167, 94, 0.28);
  border-radius: 10px;
`;

export const RoutineManagerRowTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

export const RoutineManagerRowName = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Secondary thread-title hint beside a manager row's routine name (naming
 * coherence, 2026-08-01): shown only while the thread's title still differs
 * from the routine name (the backend is converging them). Quiet and small so
 * the routine NAME stays the row's identity.
 */
export const RoutineManagerRowThreadTitle = styled.span`
  min-width: 0;
  font-size: 11px;
  font-weight: 400;
  color: rgba(122, 108, 78, 0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RoutineManagerRowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #7a6c4e;
`;

/** Last-run status dot: green ok / red error. */
export const RoutineStatusDot = styled.span<{ $status: 'ok' | 'error' }>`
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => (p.$status === 'error' ? 'var(--brand-danger)' : '#3f9d5f')};
`;

export const RoutineManagerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RoutineManagerActionButton = styled.button<{ $variant?: 'danger' }>`
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 9px;
  cursor: pointer;
  transition: filter 0.12s ease, opacity 0.12s ease;
  color: ${(p) => (p.$variant === 'danger' ? '#a13a3a' : '#6b551f')};
  background: transparent;
  border: 1px solid ${(p) => (p.$variant === 'danger' ? 'rgba(226, 74, 74, 0.5)' : 'rgba(182, 146, 76, 0.5)')};

  &:hover {
    filter: brightness(0.97);
    background: ${(p) => (p.$variant === 'danger' ? 'rgba(226, 74, 74, 0.06)' : 'rgba(198, 167, 94, 0.1)')};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid rgba(182, 146, 76, 0.6);
    outline-offset: 1px;
  }
`;

/** Inline per-row error line (action failed — the routine state is unchanged). */
export const RoutineManagerError = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #a13a3a;
`;

/**
 * Quiet panel-level notice line under the header: full-list fetch progress
 * ("Checking for more routines…") or the degraded-list warning when the fetch
 * fails ($tone="error" — the panel keeps rendering the live thread rows).
 */
export const RoutineManagerListNotice = styled.div<{ $tone?: 'error' }>`
  font-size: 11px;
  font-weight: ${(p) => (p.$tone === 'error' ? 600 : 500)};
  color: ${(p) => (p.$tone === 'error' ? '#a13a3a' : '#7a6c4e')};
  padding: 0 2px;
`;

/** Inline stop-confirm line ("history stays — only the routine stops"). */
export const RoutineManagerConfirm = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #7a6c4e;
`;

export const RoutineManagerEmptyState = styled.div`
  font-size: 12px;
  color: #6f634a;
  padding: 8px 2px;
  line-height: 1.5;
`;
